import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../app/auth';
import { fetchAPI } from '../../../services/apiClient';

// ============================================================================
// TIPOS
// ============================================================================

interface Juego {
  id: number;
  nombre: string;
  valor_unitario: number;
  activo: boolean;
}

interface Sesion {
  id: string;
  local_id: string;
  fecha: string;
  estado: string;
  created_at: string;
  closed_at: string | null;
}

interface StockRow {
  juego_id: number;
  juego_nombre: string;
  valor_unitario: number;
  inicial: number;
  ingreso: number;
  entregado_total: number;
  devuelto_total: number;
  final_fisico: number | null;
}

interface DevolucionDetalle {
  cantidad: number;
  registrado_role: string;
  created_at: string | null;
}

interface VendedorRow {
  vendedor_id: string;
  juego_id: number;
  juego_nombre: string;
  valor_unitario: number;
  entregado: number;
  devuelto: number;
  devoluciones: DevolucionDetalle[];
}

interface VendedorLocal {
  id: string;
  nombre: string;
  role: string;
}

interface ExistenciaActualItem {
  juego_id: number;
  nombre: string;
  valor_unitario: number;
  asignadas_local: number;
  entregadas_vendedores: number;
  devueltas_vendedores: number;
  existencia_actual: number;
  final_fisico: number | null;
}

interface ExistenciaActualResponse {
  local_id: string;
  sesion_id: string;
  fecha: string;
  estado: string;
  items: ExistenciaActualItem[];
}

interface InventarioMovimientoRow {
  id: string;
  juego_id: number;
  vendedor_id: string | null;
  tipo: string;
  cantidad: number;
  precio_unitario: string;
  descripcion: string | null;
  registrado_role: string | null;
  created_at: string | null;
}

interface KardexVendedorRow {
  vendedor_id: string;
  vendedor_nombre: string;
  juego_id: number;
  juego_nombre: string;
  valor_unitario: number;
  entregado: number;
  devuelto: number;
  stock_actual: number;
  vendido_reportado: number;
  disponible_real: number;
}

interface ControlInventarioForm {
  cantidad: string;
  descripcion: string;
  ajuste_manual: string;
  motivo: string;
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export const CajaLoteriaPage: React.FC = () => {
  const { user } = useAuth();
  const localId = user?.localId || 'L001';

  // Vendedores del mismo local (desde API)
  const [listaVendedores, setListaVendedores] = useState<VendedorLocal[]>([]);

  // Estado principal
  const [juegos, setJuegos] = useState<Juego[]>([]);
  const [sesion, setSesion] = useState<Sesion | null>(null);
  const [stock, setStock] = useState<StockRow[]>([]);
  const [vendedores, setVendedores] = useState<VendedorRow[]>([]);
  const [existenciaActual, setExistenciaActual] = useState<ExistenciaActualResponse | null>(null);
  const [movimientos, setMovimientos] = useState<InventarioMovimientoRow[]>([]);
  const [kardexVendedor, setKardexVendedor] = useState<KardexVendedorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mensajeOperacion, setMensajeOperacion] = useState('');
  const [controlesInventario, setControlesInventario] = useState<Record<number, ControlInventarioForm>>({});
  const [accionInventarioCargando, setAccionInventarioCargando] = useState<string | null>(null);

  // Modales
  const [modal, setModal] = useState<string | null>(null);
  const [modalJuegoId, setModalJuegoId] = useState<number>(0);
  const [modalCantidad, setModalCantidad] = useState('');
  const [modalVendedorId, setModalVendedorId] = useState('');

  // Para abrir sesion
  const [iniciales, setIniciales] = useState<Record<number, string>>({});

  // Para cierre
  const [finales, setFinales] = useState<Record<number, string>>({});
  const [devolucionesCierre, setDevolucionesCierre] = useState<Record<string, string>>({});

  // Para crear juego
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoValor, setNuevoValor] = useState('');

  // Para editar juego
  const [editJuego, setEditJuego] = useState<Juego | null>(null);
  const [editNombre, setEditNombre] = useState('');
  const [editValor, setEditValor] = useState('');

  // ============================================================================
  // CARGAR DATOS
  // ============================================================================

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [juegosData, sesionData, vendedoresData, existenciaData, movimientosData, kardexData] = await Promise.all([
        fetchAPI<Juego[]>('/loteria/juegos?activo=true'),
        fetchAPI<Sesion | null>(`/loteria/sesion/activa?local_id=${localId}`),
        fetchAPI<VendedorLocal[]>('/usuarios/local/vendedores'),
        fetchAPI<ExistenciaActualResponse>(`/caja-loteria/existencia-actual?local_id=${localId}`),
        fetchAPI<InventarioMovimientoRow[]>(`/caja-loteria/movimientos?local_id=${localId}&limit=20`),
        fetchAPI<KardexVendedorRow[]>(`/caja-loteria/kardex-vendedor?local_id=${localId}`),
      ]);

      setJuegos(juegosData);
      setSesion(sesionData);
      setListaVendedores(vendedoresData);
      setExistenciaActual(existenciaData);
      setMovimientos(movimientosData);
      setKardexVendedor(kardexData);

      if (sesionData) {
        const [stockResp, vendData] = await Promise.all([
          fetchAPI<{ stock: StockRow[] } | StockRow[]>(`/loteria/sesion/${sesionData.id}/stock`),
          fetchAPI<VendedorRow[]>(`/loteria/sesion/${sesionData.id}/vendedores`),
        ]);
        const stockData = Array.isArray(stockResp) ? stockResp : (stockResp?.stock ?? []);
        setStock(stockData);
        setVendedores(vendData);
      } else {
        setStock([]);
        setVendedores([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, [localId]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  const obtenerControlInventario = (juegoId: number): ControlInventarioForm => {
    return controlesInventario[juegoId] || {
      cantidad: '',
      descripcion: '',
      ajuste_manual: '',
      motivo: '',
    };
  };

  const actualizarControlInventario = (
    juegoId: number,
    campo: keyof ControlInventarioForm,
    valor: string,
  ) => {
    setControlesInventario((prev) => ({
      ...prev,
      [juegoId]: {
        ...obtenerControlInventario(juegoId),
        [campo]: valor,
      },
    }));
  };

  const limpiarControlInventario = (juegoId: number) => {
    setControlesInventario((prev) => ({
      ...prev,
      [juegoId]: {
        cantidad: '',
        descripcion: '',
        ajuste_manual: '',
        motivo: '',
      },
    }));
  };

  // ============================================================================
  // CARGAR INICIALES SUGERIDOS (desde última sesión cerrada)
  // ============================================================================

  const cargarInicialesSugeridos = async () => {
    try {
      const ultimaCerrada = await fetchAPI<StockRow[]>(`/loteria/sesion/ultima-cerrada?local_id=${localId}`);
      const sugeridos: Record<number, string> = {};
      ultimaCerrada.forEach(s => {
        sugeridos[s.juego_id] = String(s.inicial); // inicial viene con el final_fisico de la sesión anterior
      });
      setIniciales(sugeridos);
    } catch (e) {
      // Si no hay sesión anterior, dejar vacío
      setIniciales({});
    }
  };

  // ============================================================================
  // ACCIONES
  // ============================================================================

  const crearJuego = async () => {
    if (!nuevoNombre.trim() || !nuevoValor.trim()) return;
    try {
      await fetchAPI('/loteria/juegos', {
        method: 'POST',
        body: JSON.stringify({ nombre: nuevoNombre.trim(), valor_unitario: parseInt(nuevoValor) }),
      });
      setNuevoNombre('');
      setNuevoValor('');
      setModal(null);
      await cargarDatos();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Error');
    }
  };

  const guardarEdicionJuego = async () => {
    if (!editJuego || !editNombre.trim() || !editValor.trim()) return;
    try {
      await fetchAPI(`/loteria/juegos/${editJuego.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ nombre: editNombre.trim(), valor_unitario: parseInt(editValor) }),
      });
      setEditJuego(null);
      setModal(null);
      await cargarDatos();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Error');
    }
  };

  const eliminarJuego = async (juego: Juego) => {
    if (!confirm(`¿Eliminar "${juego.nombre}"? Se desactivará y no aparecerá más.`)) return;
    try {
      await fetchAPI(`/loteria/juegos/${juego.id}`, { method: 'DELETE' });
      await cargarDatos();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Error');
    }
  };

  const abrirSesion = async () => {
    try {
      const juegosIniciales = juegos
        .filter(j => parseInt(iniciales[j.id] || '0') > 0)
        .map(j => ({ juego_id: j.id, inicial: parseInt(iniciales[j.id] || '0') }));

      if (juegosIniciales.length === 0) {
        alert('Debe ingresar al menos un juego con cantidad inicial');
        return;
      }

      await fetchAPI('/loteria/sesion/abrir', {
        method: 'POST',
        body: JSON.stringify({ local_id: localId, juegos_iniciales: juegosIniciales }),
      });
      setModal(null);
      setIniciales({});
      await cargarDatos();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Error');
    }
  };

  const ejecutarMovimiento = async (tipo: string) => {
    if (!modalJuegoId || !modalCantidad.trim()) return;
    try {
      const body: Record<string, unknown> = {
        sesion_id: sesion!.id,
        juego_id: modalJuegoId,
        cantidad: parseInt(modalCantidad),
      };
      if (tipo === 'entrega' || tipo === 'devolucion') {
        if (!modalVendedorId.trim()) {
          alert('Debe indicar el vendedor');
          return;
        }
        body.vendedor_id = modalVendedorId.trim();
      }
      await fetchAPI(`/loteria/movimientos/${tipo}`, { method: 'POST', body: JSON.stringify(body) });
      cerrarModal();
      await cargarDatos();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Error');
    }
  };

  const cerrarSesion = async () => {
    if (!sesion) return;

    // 1. Procesar devoluciones pendientes del cierre
    for (const [key, val] of Object.entries(devolucionesCierre)) {
      const cant = parseInt(val);
      if (!cant || cant <= 0) continue;
      const [vendedorId, juegoId] = key.split('|');
      try {
        await fetchAPI('/loteria/movimientos/devolucion', {
          method: 'POST',
          body: JSON.stringify({ sesion_id: sesion.id, juego_id: parseInt(juegoId), vendedor_id: vendedorId, cantidad: cant }),
        });
      } catch (e) {
        alert(`Error devolución ${vendedorId}: ${e instanceof Error ? e.message : 'Error'}`);
        return;
      }
    }

    // Recargar datos después de devoluciones para que el resumen refleje los nuevos números
    if (Object.values(devolucionesCierre).some(v => parseInt(v) > 0)) {
      await cargarDatos();
    }

    // 2. Cerrar sesión con finales físicos
    const finalesList = stock.map(s => ({
      juego_id: s.juego_id,
      final_fisico: parseInt(finales[s.juego_id]?.toString() || '0'),
    }));
    try {
      await fetchAPI('/loteria/sesion/cerrar', {
        method: 'POST',
        body: JSON.stringify({ sesion_id: sesion.id, finales: finalesList }),
      });
      setModal(null);
      setFinales({});
      setDevolucionesCierre({});
      await cargarDatos();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Error');
    }
  };

  const cerrarModal = () => {
    setModal(null);
    setModalJuegoId(0);
    setModalCantidad('');
    setModalVendedorId('');
  };

  const ejecutarAccionInventarioLocal = async (
    item: ExistenciaActualItem,
    accion: 'asignar' | 'devolver' | 'ajustar',
  ) => {
    const control = obtenerControlInventario(item.juego_id);
    const cantidad = parseInt(control.cantidad || '0', 10);
    const ajusteManual = parseInt(control.ajuste_manual || '0', 10);
    const descripcion = control.descripcion.trim();
    const motivo = control.motivo.trim();
    const accionKey = `${accion}-${item.juego_id}`;

    setError('');
    setMensajeOperacion('');

    try {
      if (accion === 'asignar') {
        if (cantidad <= 0) {
          setError(`Debe indicar una cantidad válida para asignar ${item.nombre}.`);
          return;
        }
        setAccionInventarioCargando(accionKey);
        await fetchAPI('/caja-loteria/asignar-local', {
          method: 'POST',
          body: JSON.stringify({
            local_id: localId,
            juego_id: item.juego_id,
            cantidad,
            descripcion: descripcion || `Asignación a local de ${item.nombre}`,
          }),
        });
        setMensajeOperacion(`Asignación registrada para ${item.nombre}.`);
      }

      if (accion === 'devolver') {
        if (cantidad <= 0) {
          setError(`Debe indicar una cantidad válida para devolver ${item.nombre}.`);
          return;
        }
        if (cantidad > item.existencia_actual) {
          setError(`No puede devolver más de la existencia actual de ${item.nombre}.`);
          return;
        }
        setAccionInventarioCargando(accionKey);
        await fetchAPI('/caja-loteria/devolucion-local', {
          method: 'POST',
          body: JSON.stringify({
            local_id: localId,
            juego_id: item.juego_id,
            cantidad,
            descripcion: descripcion || `Devolución de local de ${item.nombre}`,
          }),
        });
        setMensajeOperacion(`Devolución registrada para ${item.nombre}.`);
      }

      if (accion === 'ajustar') {
        if (!motivo) {
          setError(`Debe indicar el motivo del ajuste para ${item.nombre}.`);
          return;
        }
        if (!Number.isInteger(ajusteManual) || ajusteManual === 0) {
          setError(`Debe indicar un ajuste manual distinto de cero para ${item.nombre}.`);
          return;
        }
        if (ajusteManual < 0 && Math.abs(ajusteManual) > item.existencia_actual) {
          setError(`El ajuste dejaría stock negativo en ${item.nombre}.`);
          return;
        }
        setAccionInventarioCargando(accionKey);
        await fetchAPI('/caja-loteria/ajuste', {
          method: 'POST',
          body: JSON.stringify({
            local_id: localId,
            juego_id: item.juego_id,
            ajuste_manual: ajusteManual,
            // retrocompatibilidad por si backend viejo está desplegado
            delta: ajusteManual,
            motivo,
          }),
        });
        setMensajeOperacion(`Ajuste registrado para ${item.nombre}.`);
      }

      limpiarControlInventario(item.juego_id);
      await cargarDatos();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error registrando movimiento de inventario');
    } finally {
      setAccionInventarioCargando(null);
    }
  };

  // ============================================================================
  // CALCULOS
  // ============================================================================

  const calcVendido = (r: StockRow) => r.entregado_total - r.devuelto_total;
  const calcDisponible = (r: StockRow) => r.inicial + r.ingreso - r.entregado_total + r.devuelto_total;

  // Resolver nombre de vendedor por ID
  const nombreVendedor = (id: string) => {
    const u = listaVendedores.find(v => v.id === id);
    return u ? u.nombre : id;
  };

  // Agrupar vendedores por vendedor_id para totales
  const vendedorIds = [...new Set(vendedores.map(v => v.vendedor_id))];

  // ============================================================================
  // RENDER
  // ============================================================================

  if (loading && !sesion) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Cargando...</p>
      </div>
    );
  }

  const cerrada = sesion?.estado === 'CERRADA';
  const inventarioEditable = (user?.role === 'LOCAL' || user?.role === 'BANCA') && existenciaActual?.estado !== 'CERRADA';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Sesión operativa</p>
          <p className="text-lg font-bold text-gray-900">{existenciaActual?.estado || 'SIN SESIÓN'}</p>
        </div>
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-blue-600 mb-1">Juegos activos</p>
          <p className="text-2xl font-bold text-blue-700">{existenciaActual?.items.length || 0}</p>
        </div>
        <div className="bg-amber-50 rounded-xl border border-amber-200 p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-amber-600 mb-1">Existencia total</p>
          <p className="text-2xl font-bold text-amber-700">{(existenciaActual?.items || []).reduce((sum, item) => sum + item.existencia_actual, 0)}</p>
        </div>
        <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-emerald-600 mb-1">Valor estimado</p>
          <p className="text-2xl font-bold text-emerald-700">₡{(existenciaActual?.items || []).reduce((sum, item) => sum + item.existencia_actual * item.valor_unitario, 0).toLocaleString()}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-900">Existencia persistente por local</h2>
          <span className="text-xs text-gray-500">Sesión {existenciaActual?.estado || 'N/A'}</span>
        </div>
        <div className="px-4 py-3 border-b border-gray-100 bg-slate-50 text-xs text-slate-600">
          {inventarioEditable
            ? 'Operación rápida: use la misma fila para asignar, devolver o ajustar inventario sin salir de la pantalla.'
            : 'Vista operativa en solo lectura.'}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Juego</th>
                <th className="px-4 py-3 text-right font-semibold">Asignadas</th>
                <th className="px-4 py-3 text-right font-semibold">Entregadas</th>
                <th className="px-4 py-3 text-right font-semibold">Devueltas</th>
                <th className="px-4 py-3 text-right font-semibold">Existencia</th>
                {inventarioEditable && <th className="px-4 py-3 text-left font-semibold min-w-[360px]">Controles</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(existenciaActual?.items || []).map((item) => (
                <tr key={item.juego_id}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{item.nombre}</div>
                    <div className="text-xs text-gray-500">₡{item.valor_unitario.toLocaleString()} c/u</div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{item.asignadas_local}</td>
                  <td className="px-4 py-3 text-right font-mono">{item.entregadas_vendedores}</td>
                  <td className="px-4 py-3 text-right font-mono">{item.devueltas_vendedores}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-gray-900">{item.existencia_actual}</td>
                  {inventarioEditable && (
                    <td className="px-4 py-3">
                      <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <div className="grid grid-cols-1 md:grid-cols-[110px_minmax(0,1fr)_auto_auto] gap-2 items-center">
                          <input
                            type="number"
                            min="1"
                            value={obtenerControlInventario(item.juego_id).cantidad}
                            onChange={(e) => actualizarControlInventario(item.juego_id, 'cantidad', e.target.value)}
                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm text-right font-mono"
                            placeholder="Cantidad"
                          />
                          <input
                            type="text"
                            value={obtenerControlInventario(item.juego_id).descripcion}
                            onChange={(e) => actualizarControlInventario(item.juego_id, 'descripcion', e.target.value)}
                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                            placeholder="Nota opcional"
                          />
                          <button
                            onClick={() => ejecutarAccionInventarioLocal(item, 'asignar')}
                            disabled={accionInventarioCargando === `asignar-${item.juego_id}`}
                            className="rounded bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:bg-gray-400"
                          >
                            {accionInventarioCargando === `asignar-${item.juego_id}` ? '...' : 'Asignar'}
                          </button>
                          <button
                            onClick={() => ejecutarAccionInventarioLocal(item, 'devolver')}
                            disabled={accionInventarioCargando === `devolver-${item.juego_id}`}
                            className="rounded bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:bg-gray-400"
                          >
                            {accionInventarioCargando === `devolver-${item.juego_id}` ? '...' : 'Devolver'}
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-[110px_minmax(0,1fr)_auto] gap-2 items-center">
                          <input
                            type="number"
                            value={obtenerControlInventario(item.juego_id).ajuste_manual}
                            onChange={(e) => actualizarControlInventario(item.juego_id, 'ajuste_manual', e.target.value)}
                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm text-right font-mono"
                            placeholder="Ajuste (+/-)"
                          />
                          <input
                            type="text"
                            value={obtenerControlInventario(item.juego_id).motivo}
                            onChange={(e) => actualizarControlInventario(item.juego_id, 'motivo', e.target.value)}
                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                            placeholder="Motivo del ajuste"
                          />
                          <button
                            onClick={() => ejecutarAccionInventarioLocal(item, 'ajustar')}
                            disabled={accionInventarioCargando === `ajustar-${item.juego_id}`}
                            className="rounded bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-900 disabled:bg-gray-400"
                          >
                            {accionInventarioCargando === `ajustar-${item.juego_id}` ? '...' : 'Ajuste'}
                          </button>
                        </div>
                        <p className="text-[11px] text-gray-500">
                          Ajuste manual positivo suma inventario. Ajuste manual negativo descuenta inventario.
                        </p>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {user?.role !== 'LOCAL' && (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-base font-bold text-gray-900">Movimientos de inventario</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Fecha</th>
                <th className="px-4 py-3 text-left font-semibold">Tipo</th>
                <th className="px-4 py-3 text-left font-semibold">Detalle</th>
                <th className="px-4 py-3 text-right font-semibold">Cantidad</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {movimientos.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-gray-500">No hay movimientos registrados</td>
                </tr>
              ) : movimientos.map((mov) => (
                <tr key={mov.id}>
                  <td className="px-4 py-3 text-gray-600">{mov.created_at?.slice(0, 19).replace('T', ' ') || '-'}</td>
                  <td className="px-4 py-3"><span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">{mov.tipo}</span></td>
                  <td className="px-4 py-3 text-gray-700">{mov.descripcion || `Juego #${mov.juego_id}`}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-gray-900">{mov.cantidad}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-base font-bold text-gray-900">Kardex persistente por vendedor</h2>
          <p className="text-xs text-gray-500 mt-1">Acumulado histórico sin reinicio diario (entregado - devuelto - vendido reportado).</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Vendedor</th>
                <th className="px-4 py-3 text-left font-semibold">Juego</th>
                <th className="px-4 py-3 text-right font-semibold">Entregado</th>
                <th className="px-4 py-3 text-right font-semibold">Devuelto</th>
                <th className="px-4 py-3 text-right font-semibold">Vendido</th>
                <th className="px-4 py-3 text-right font-semibold">Disponible real</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {kardexVendedor.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-gray-500">Sin datos de kardex acumulado</td>
                </tr>
              ) : kardexVendedor.map((row) => (
                <tr key={`${row.vendedor_id}-${row.juego_id}`}>
                  <td className="px-4 py-3 text-gray-800">{row.vendedor_nombre}</td>
                  <td className="px-4 py-3 text-gray-700">{row.juego_nombre}</td>
                  <td className="px-4 py-3 text-right font-mono">{row.entregado}</td>
                  <td className="px-4 py-3 text-right font-mono">{row.devuelto}</td>
                  <td className="px-4 py-3 text-right font-mono">{row.vendido_reportado}</td>
                  <td className={`px-4 py-3 text-right font-mono font-semibold ${row.disponible_real < 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                    {row.disponible_real}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Caja Loterías</h1>
          <p className="text-sm text-gray-500">
            {new Date().toLocaleDateString('es-CR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            {sesion && (
              <span className={`ml-2 px-2 py-0.5 rounded text-xs font-bold ${cerrada ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                {sesion.estado}
              </span>
            )}
          </p>
        </div>
        {loading && <span className="text-xs text-blue-500">Actualizando...</span>}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded text-sm">{error}</div>
      )}

      {mensajeOperacion && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded text-sm">{mensajeOperacion}</div>
      )}

      {/* BOTONES */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setModal('crear_juego')} className="px-3 py-1.5 bg-gray-600 text-white rounded text-sm hover:bg-gray-700">
          + Juego
        </button>
        {!sesion && (
          <button onClick={() => { cargarInicialesSugeridos(); setModal('abrir'); }} className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
            Abrir Sesión
          </button>
        )}
        {sesion && !cerrada && (
          <>
            <button onClick={() => setModal('ingreso')} className="px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700">
              Registrar Ingreso
            </button>
            <button onClick={() => { setModalVendedorId(''); setModalJuegoId(0); setModalCantidad(''); setModal('entrega'); }} className="px-3 py-1.5 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700">
              + Entrega a Vendedor
            </button>
            <button onClick={() => { setModalVendedorId(''); setModalJuegoId(0); setModalCantidad(''); setModal('devolucion'); }} className="px-3 py-1.5 bg-orange-600 text-white rounded text-sm hover:bg-orange-700">
              Devolución
            </button>
            <button onClick={() => { setFinales({}); setDevolucionesCierre({}); setModal('cerrar'); }} className="px-3 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700">
              Cerrar Loterías
            </button>
          </>
        )}
      </div>

      {/* ============ SECCION: JUEGOS DE LOTERIA ============ */}
      {juegos.length > 0 && (
        <div className="bg-white rounded border border-gray-200">
          <div className="px-4 py-2 border-b border-gray-200 bg-gray-50">
            <h2 className="text-sm font-bold text-gray-700">JUEGOS DE LOTERIA</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {juegos.map(j => (
              <div key={j.id} className="flex items-center justify-between px-4 py-2">
                <div>
                  <span className="text-sm font-medium text-gray-800">{j.nombre}</span>
                  <span className="text-xs text-gray-500 ml-2">({j.valor_unitario.toLocaleString()}/u)</span>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => { setEditJuego(j); setEditNombre(j.nombre); setEditValor(String(j.valor_unitario)); setModal('editar_juego'); }}
                    className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => eliminarJuego(j)}
                    className="px-2 py-1 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100"
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ============ SECCION A: TABLA POR JUEGO ============ */}
      {sesion && stock.length > 0 && (
        <div className="bg-white rounded border border-gray-200 overflow-x-auto">
          <div className="px-4 py-2 border-b border-gray-200 bg-gray-50">
            <h2 className="text-sm font-bold text-gray-700">STOCK POR JUEGO</h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 border-b">
                <th className="text-left px-3 py-2 font-semibold">Juego</th>
                <th className="text-right px-3 py-2 font-semibold">Val.Unit</th>
                <th className="text-right px-3 py-2 font-semibold">Inicial</th>
                <th className="text-right px-3 py-2 font-semibold">Ingreso</th>
                <th className="text-right px-3 py-2 font-semibold">Entregado</th>
                <th className="text-right px-3 py-2 font-semibold">Devuelto</th>
                <th className="text-right px-3 py-2 font-semibold bg-blue-50">Vendido</th>
                <th className="text-right px-3 py-2 font-semibold bg-yellow-50">Disponible</th>
                <th className="text-right px-3 py-2 font-semibold bg-gray-200">Final Físico</th>
              </tr>
            </thead>
            <tbody>
              {stock.map(r => (
                <tr key={r.juego_id} className="border-b hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium">{r.juego_nombre}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{r.valor_unitario.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">{r.inicial}</td>
                  <td className="px-3 py-2 text-right">{r.ingreso}</td>
                  <td className="px-3 py-2 text-right">{r.entregado_total}</td>
                  <td className="px-3 py-2 text-right">{r.devuelto_total}</td>
                  <td className="px-3 py-2 text-right font-bold bg-blue-50">{calcVendido(r)}</td>
                  <td className="px-3 py-2 text-right font-bold bg-yellow-50">{calcDisponible(r)}</td>
                  <td className="px-3 py-2 text-right bg-gray-100 font-mono">
                    {r.final_fisico !== null ? r.final_fisico : '—'}
                  </td>
                </tr>
              ))}
              {/* Fila de totales */}
              <tr className="bg-gray-100 font-bold border-t-2">
                <td className="px-3 py-2">TOTAL</td>
                <td className="px-3 py-2 text-right"></td>
                <td className="px-3 py-2 text-right">{stock.reduce((a, r) => a + r.inicial, 0)}</td>
                <td className="px-3 py-2 text-right">{stock.reduce((a, r) => a + r.ingreso, 0)}</td>
                <td className="px-3 py-2 text-right">{stock.reduce((a, r) => a + r.entregado_total, 0)}</td>
                <td className="px-3 py-2 text-right">{stock.reduce((a, r) => a + r.devuelto_total, 0)}</td>
                <td className="px-3 py-2 text-right bg-blue-50">{stock.reduce((a, r) => a + calcVendido(r), 0)}</td>
                <td className="px-3 py-2 text-right bg-yellow-50">{stock.reduce((a, r) => a + calcDisponible(r), 0)}</td>
                <td className="px-3 py-2 text-right bg-gray-200"></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* ============ SECCION B: TABLA POR VENDEDOR ============ */}
      {sesion && vendedores.length > 0 && (
        <div className="bg-white rounded border border-gray-200 overflow-x-auto">
          <div className="px-4 py-2 border-b border-gray-200 bg-gray-50">
            <h2 className="text-sm font-bold text-gray-700">DETALLE POR VENDEDOR</h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 border-b">
                <th className="text-left px-3 py-2 font-semibold">Vendedor</th>
                <th className="text-left px-3 py-2 font-semibold">Juego</th>
                <th className="text-right px-3 py-2 font-semibold">Entregado</th>
                <th className="text-right px-3 py-2 font-semibold">Devuelto</th>
                <th className="text-right px-3 py-2 font-semibold bg-blue-50">Vendido</th>
                <th className="text-right px-3 py-2 font-semibold bg-green-50">Cobro</th>
                {!cerrada && <th className="text-center px-3 py-2 font-semibold">Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {vendedorIds.map(vid => {
                const rows = vendedores.filter(v => v.vendedor_id === vid);
                const totalCobro = rows.reduce((a, v) => a + (v.entregado - v.devuelto) * v.valor_unitario, 0);
                return (
                  <React.Fragment key={vid}>
                    {rows.map((v, i) => {
                      const vendido = v.entregado - v.devuelto;
                      const cobro = vendido * v.valor_unitario;
                      return (
                        <tr key={`${vid}-${v.juego_id}`} className="border-b hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium">{i === 0 ? nombreVendedor(vid) : ''}</td>
                          <td className="px-3 py-2">{v.juego_nombre}</td>
                          <td className="px-3 py-2 text-right">{v.entregado}</td>
                          <td className="px-3 py-2 text-right">
                            {v.devuelto}
                            {v.devoluciones && v.devoluciones.length > 0 && (
                              <div className="mt-0.5">
                                {v.devoluciones.map((d, di) => (
                                  <span key={di} className={`inline-block text-[10px] rounded px-1 mr-0.5 mb-0.5 ${
                                    d.registrado_role === 'VENDEDOR_CON_CAJA'
                                      ? 'bg-purple-100 text-purple-700'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {d.cantidad} {d.registrado_role === 'VENDEDOR_CON_CAJA' ? 'vendedor' : 'local'}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-bold bg-blue-50">{vendido}</td>
                          <td className="px-3 py-2 text-right bg-green-50">{cobro.toLocaleString()}</td>
                          {!cerrada && (
                            <td className="px-3 py-2 text-center">
                              <button
                                onClick={() => { setModalVendedorId(vid); setModalJuegoId(v.juego_id); setModalCantidad(''); setModal('entrega'); }}
                                className="px-2 py-0.5 bg-yellow-500 text-white rounded text-xs hover:bg-yellow-600 mr-1"
                              >Entregar</button>
                              <button
                                onClick={() => { setModalVendedorId(vid); setModalJuegoId(v.juego_id); setModalCantidad(''); setModal('devolucion'); }}
                                className="px-2 py-0.5 bg-orange-500 text-white rounded text-xs hover:bg-orange-600"
                              >Devolver</button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    {/* Subtotal vendedor */}
                    <tr className="bg-gray-50 font-semibold border-b">
                      <td className="px-3 py-1 text-right text-xs text-gray-500" colSpan={!cerrada ? 6 : 5}>
                        Total {nombreVendedor(vid)}:
                      </td>
                      <td className="px-3 py-1 text-right bg-green-100 font-bold">
                        {totalCobro.toLocaleString()}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Mensaje si no hay sesion */}
      {!sesion && !loading && (
        <div className="bg-white rounded border border-gray-200 text-center py-12 text-gray-500">
          <p className="text-lg">No hay sesión de inventario activa</p>
          <p className="text-sm mt-1">Presione "Abrir Sesión" para continuar el inventario persistente</p>
        </div>
      )}

      {/* ============ MODALES ============ */}

      {/* MODAL: Crear Juego */}
      {modal === 'crear_juego' && (
        <Modal title="Crear Juego" onClose={cerrarModal}>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
              <input type="text" value={nuevoNombre} onChange={e => setNuevoNombre(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm" placeholder="Ej: Chances Martes" autoFocus />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valor Unitario</label>
              <input type="number" value={nuevoValor} onChange={e => setNuevoValor(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm" placeholder="Ej: 1000" />
            </div>
            <button onClick={crearJuego} className="w-full bg-blue-600 text-white rounded py-2 text-sm font-medium hover:bg-blue-700">
              Crear
            </button>
          </div>
        </Modal>
      )}

      {/* MODAL: Editar Juego */}
      {modal === 'editar_juego' && editJuego && (
        <Modal title="Editar Juego" onClose={() => { setEditJuego(null); cerrarModal(); }}>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
              <input type="text" value={editNombre} onChange={e => setEditNombre(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm" autoFocus />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valor Unitario</label>
              <input type="number" value={editValor} onChange={e => setEditValor(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm" />
            </div>
            <button onClick={guardarEdicionJuego} className="w-full bg-blue-600 text-white rounded py-2 text-sm font-medium hover:bg-blue-700">
              Guardar
            </button>
          </div>
        </Modal>
      )}

      {/* MODAL: Abrir Sesion */}
      {modal === 'abrir' && (
        <Modal title="Abrir Sesión del Día" onClose={cerrarModal}>
          <p className="text-xs text-gray-500 mb-1">Ingrese la cantidad inicial de cada juego:</p>
          <p className="text-xs text-blue-600 mb-3">Los valores pre-llenados son del cierre anterior.</p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {juegos.map(j => (
              <div key={j.id} className="flex items-center gap-3">
                <span className="text-sm flex-1">{j.nombre}</span>
                <input type="number" value={iniciales[j.id] || ''}
                  onChange={e => setIniciales(prev => ({ ...prev, [j.id]: e.target.value }))}
                  className="w-24 border rounded px-2 py-1 text-sm text-right" placeholder="0" />
              </div>
            ))}
          </div>
          {juegos.length === 0 && (
            <p className="text-sm text-yellow-600">No hay juegos creados. Cree juegos primero.</p>
          )}
          <button onClick={abrirSesion} disabled={juegos.length === 0}
            className="w-full mt-3 bg-blue-600 text-white rounded py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            Abrir
          </button>
        </Modal>
      )}

      {/* MODAL: Ingreso */}
      {modal === 'ingreso' && (
        <Modal title="Registrar Ingreso (Local)" onClose={cerrarModal}>
          <div className="space-y-3">
            <SelectJuego stock={stock} value={modalJuegoId} onChange={setModalJuegoId} />
            <InputCantidad value={modalCantidad} onChange={setModalCantidad} />
            <button onClick={() => ejecutarMovimiento('ingreso')} className="w-full bg-green-600 text-white rounded py-2 text-sm font-medium hover:bg-green-700">
              Registrar
            </button>
          </div>
        </Modal>
      )}

      {/* MODAL: Entrega */}
      {modal === 'entrega' && (
        <Modal title="Entrega a Vendedor" onClose={cerrarModal}>
          <div className="space-y-3">
            <SelectVendedor value={modalVendedorId} onChange={setModalVendedorId} vendedores={listaVendedores} />
            <SelectJuego stock={stock} value={modalJuegoId} onChange={setModalJuegoId} />
            <InputCantidad value={modalCantidad} onChange={setModalCantidad} />
            <button onClick={() => ejecutarMovimiento('entrega')} className="w-full bg-yellow-600 text-white rounded py-2 text-sm font-medium hover:bg-yellow-700">
              Entregar
            </button>
          </div>
        </Modal>
      )}

      {/* MODAL: Devolucion */}
      {modal === 'devolucion' && (
        <Modal title="Devolución de Vendedor" onClose={cerrarModal}>
          <div className="space-y-3">
            <SelectVendedor value={modalVendedorId} onChange={setModalVendedorId} vendedores={listaVendedores} />
            <SelectJuego stock={stock} value={modalJuegoId} onChange={setModalJuegoId} />
            <InputCantidad value={modalCantidad} onChange={setModalCantidad} />
            <button onClick={() => ejecutarMovimiento('devolucion')} className="w-full bg-orange-600 text-white rounded py-2 text-sm font-medium hover:bg-orange-700">
              Registrar Devolución
            </button>
          </div>
        </Modal>
      )}

      {/* MODAL: Cerrar */}
      {modal === 'cerrar' && (
        <Modal title="Cerrar Loterías" onClose={cerrarModal}>

          {/* Sección 1: Devoluciones de vendedores */}
          {vendedores.filter(v => v.entregado - v.devuelto > 0).length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-bold text-gray-700 mb-2">Devoluciones de vendedores:</p>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {vendedores.filter(v => v.entregado - v.devuelto > 0).map(v => {
                  const pendiente = v.entregado - v.devuelto;
                  const key = `${v.vendedor_id}|${v.juego_id}`;
                  return (
                    <div key={key} className="flex items-center gap-2 text-sm">
                      <span className="flex-1">{nombreVendedor(v.vendedor_id)} - {v.juego_nombre}</span>
                      <span className="text-xs text-gray-400">tiene {pendiente}</span>
                      <input type="number" value={devolucionesCierre[key] ?? ''}
                        onChange={e => setDevolucionesCierre(prev => ({ ...prev, [key]: e.target.value }))}
                        className="w-20 border rounded px-2 py-1 text-sm text-right" placeholder="0" min="0" max={pendiente} />
                    </div>
                  );
                })}
              </div>
              <hr className="mt-3" />
            </div>
          )}

          {/* Sección 2: Final físico */}
          <p className="text-xs text-gray-500 mb-3">Ingrese el conteo final físico por juego:</p>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {stock.map(s => {
              const esperado = calcDisponible(s);
              const finalVal = parseInt(finales[s.juego_id]?.toString() || '');
              const hasFinal = !isNaN(finalVal);
              const diferencia = hasFinal ? finalVal - esperado : 0;
              return (
                <div key={s.juego_id}>
                  <div className="flex items-center gap-3">
                    <span className="text-sm flex-1">{s.juego_nombre}</span>
                    <span className="text-xs text-gray-400">Esperado: {esperado}</span>
                    <input type="number" value={finales[s.juego_id] ?? ''}
                      onChange={e => setFinales(prev => ({ ...prev, [s.juego_id]: e.target.value }))}
                      className="w-24 border rounded px-2 py-1 text-sm text-right" placeholder={String(esperado)} />
                  </div>
                  {hasFinal && diferencia !== 0 && (
                    <p className="text-xs text-yellow-700 bg-yellow-50 px-2 py-1 rounded mt-1">
                      {'\u26A0'} Diferencia {diferencia > 0 ? '+' : ''}{diferencia} (se registrará ajuste automático)
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Resumen de liquidación */}
          <div className="mt-4 border-t pt-3">
            <p className="text-xs font-bold text-gray-700 mb-2">Resumen de liquidación:</p>
            <div className="space-y-1 text-sm">
              {stock.map(s => {
                const vendido = calcVendido(s);
                const cobro = vendido * s.valor_unitario;
                return (
                  <div key={s.juego_id} className="flex justify-between">
                    <span>{s.juego_nombre}</span>
                    <span>vendido {vendido} {'\u2192'} {'\u20A1'}{cobro.toLocaleString()}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between font-bold text-sm mt-2 pt-2 border-t">
              <span>TOTAL COBRO</span>
              <span>{'\u20A1'}{stock.reduce((a, s) => a + calcVendido(s) * s.valor_unitario, 0).toLocaleString()}</span>
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <button onClick={cerrarModal} className="flex-1 bg-gray-200 text-gray-700 rounded py-2 text-sm font-medium hover:bg-gray-300">
              Cancelar
            </button>
            <button onClick={cerrarSesion} className="flex-1 bg-red-600 text-white rounded py-2 text-sm font-medium hover:bg-red-700">
              Confirmar Cierre
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ============================================================================
// COMPONENTES AUXILIARES (inline, sin archivos separados)
// ============================================================================

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-5" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-bold text-gray-900">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
      </div>
      {children}
    </div>
  </div>
);

const SelectJuego: React.FC<{ stock: StockRow[]; value: number; onChange: (v: number) => void }> = ({ stock, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">Juego</label>
    <select value={value} onChange={e => onChange(parseInt(e.target.value))}
      className="w-full border rounded px-3 py-2 text-sm">
      <option value={0}>-- Seleccionar --</option>
      {stock.map(s => (
        <option key={s.juego_id} value={s.juego_id}>{s.juego_nombre}</option>
      ))}
    </select>
  </div>
);

const InputCantidad: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">Cantidad</label>
    <input type="number" value={value} onChange={e => onChange(e.target.value)}
      className="w-full border rounded px-3 py-2 text-sm" placeholder="0" min="1" autoFocus />
  </div>
);

const SelectVendedor: React.FC<{ value: string; onChange: (v: string) => void; vendedores: { id: string; nombre: string }[] }> = ({ value, onChange, vendedores }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">Vendedor</label>
    <select value={value} onChange={e => onChange(e.target.value)}
      className="w-full border rounded px-3 py-2 text-sm">
      <option value="">-- Seleccionar --</option>
      {vendedores.map(v => (
        <option key={v.id} value={v.id}>{v.nombre}</option>
      ))}
    </select>
  </div>
);

export default CajaLoteriaPage;
